/**
 * Dashboard — a real overview: profile status, CV library, applications,
 * and the next best action. Replaces the old static placeholder.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { API_BASE as API, getToken } from '../config';

// Detect the profile's country from its free-text location — country names
// first, then well-known cities. Must produce values the Jobs filter accepts.
const CITY_TO_COUNTRY: Record<string, string> = {
  madrid: 'Spain', barcelona: 'Spain', valencia: 'Spain', sevilla: 'Spain', bilbao: 'Spain',
  brussels: 'Belgium', antwerp: 'Belgium', ghent: 'Belgium',
  amsterdam: 'Netherlands', rotterdam: 'Netherlands', utrecht: 'Netherlands',
  zurich: 'Switzerland', geneva: 'Switzerland', basel: 'Switzerland', bern: 'Switzerland',
  paris: 'France', lyon: 'France',
  berlin: 'Germany', munich: 'Germany', hamburg: 'Germany', frankfurt: 'Germany',
  lisbon: 'Portugal', porto: 'Portugal',
  london: 'United Kingdom', manchester: 'United Kingdom', edinburgh: 'United Kingdom',
  dublin: 'Ireland', vienna: 'Austria', stockholm: 'Sweden', gothenburg: 'Sweden',
  copenhagen: 'Denmark', helsinki: 'Finland', oslo: 'Norway', warsaw: 'Poland',
  milan: 'Italy', rome: 'Italy', athens: 'Greece', bucharest: 'Romania',
};
const COUNTRY_NAMES = [
  'Austria', 'Belgium', 'Canada', 'Denmark', 'Finland', 'France', 'Germany',
  'Greece', 'Ireland', 'Italy', 'Netherlands', 'Norway', 'Poland', 'Portugal',
  'Romania', 'Spain', 'Sweden', 'Switzerland', 'United Kingdom', 'United States',
];

function detectCountry(profile: any): string {
  const hay = `${profile?.location ?? ''} ${profile?.citizenship ?? ''}`.toLowerCase();
  for (const c of COUNTRY_NAMES) if (hay.includes(c.toLowerCase())) return c;
  for (const [city, c] of Object.entries(CITY_TO_COUNTRY)) if (hay.includes(city)) return c;
  return '';
}

interface CVRow { id: number; title: string; created_at: string; truth_report: { score: number } | null }
interface AppRow { id: number; job_title: string; company: string; status: string; applied_at: string }

const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [cvs, setCvs] = useState<CVRow[]>([]);
  const [apps, setApps] = useState<AppRow[]>([]);
  const [oneShotBusy, setOneShotBusy] = useState(false);

  // One click: general CV from the saved profile (auto-saved to My CVs, the
  // user refines it later) + jump to Jobs pre-filtered to their country and
  // title, sorted by best fit.
  const oneShot = async () => {
    if (!profile) { navigate('/app/profile-setup'); return; }
    setOneShotBusy(true);
    try {
      const res = await fetch(`${API}/api/cv-profile/generate`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: 'general', run_truth_check: false }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'CV generation failed');
      }
      const country = detectCountry(profile);
      const query = profile.current_title || (profile.skills?.technical?.[0] ?? '');
      try {
        sessionStorage.setItem('jobsPageState', JSON.stringify({
          autorun: true, query, country, sortBy: 'fit', posted: '30',
          jobs: [], total: 0, searched: false, analyses: {}, applied: {},
        }));
      } catch {}
      toast.success(`CV saved to My CVs — now finding ${country || 'matching'} jobs…`);
      navigate('/app/jobs');
    } catch (e: any) {
      toast.error(e.message ?? 'One-shot setup failed.');
    } finally {
      setOneShotBusy(false);
    }
  };

  useEffect(() => {
    fetch(`${API}/api/cv-profile/`, { headers: authHeaders() })
      .then(r => r.json()).then(p => { setProfile(p || null); setProfileLoaded(true); })
      .catch(() => setProfileLoaded(true));
    fetch(`${API}/api/cvs/`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setCvs(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/api/jobs/applications`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setApps(d.applications || [])).catch(() => {});
  }, []);

  // Profile completeness — the fields that actually drive CV quality + analysis.
  const checks: [string, boolean][] = profile ? [
    ['Summary', !!profile.professional_summary],
    ['Work experience', (profile.work_experience ?? []).length > 0],
    ['Skills', ((profile.skills?.technical ?? []).length + (profile.skills?.tools ?? []).length) > 0],
    ['Education', (profile.education ?? []).length > 0],
    ['Citizenship (for visa checks)', !!profile.citizenship],
  ] : [];
  const done = checks.filter(([, ok]) => ok).length;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h1>

      {/* Next best action */}
      {profileLoaded && !profile && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-5 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="font-semibold text-blue-900">Start here: set up your profile</p>
            <p className="text-sm text-blue-700">Upload your CV or answer a few questions — everything else builds on it.</p>
          </div>
          <Link to="/app/profile-setup" className="px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700">
            Set up profile →
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* Profile */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Profile</h3>
            <Link to="/app/profile-setup" className="text-xs text-blue-600 hover:underline">Edit</Link>
          </div>
          {profile ? (
            <>
              <p className="text-3xl font-bold text-blue-600 mb-2">{done}/{checks.length}</p>
              <ul className="space-y-1">
                {checks.map(([label, ok]) => (
                  <li key={label} className={`text-xs flex items-center gap-1.5 ${ok ? 'text-gray-500' : 'text-amber-600'}`}>
                    <span>{ok ? '✓' : '○'}</span>{label}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-sm text-gray-400">{profileLoaded ? 'Not set up yet' : 'Loading…'}</p>
          )}
        </div>

        {/* CVs */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">CVs generated</h3>
            <Link to="/app/cvs" className="text-xs text-blue-600 hover:underline">View all</Link>
          </div>
          <p className="text-3xl font-bold text-green-600 mb-2">{cvs.length}</p>
          <ul className="space-y-1">
            {cvs.slice(0, 3).map(cv => (
              <li key={cv.id} className="text-xs text-gray-500 truncate">
                {cv.title}
                {cv.truth_report && <span className="text-gray-400"> · {cv.truth_report.score}/100</span>}
              </li>
            ))}
          </ul>
        </div>

        {/* Applications */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Applications</h3>
            <Link to="/app/applications" className="text-xs text-blue-600 hover:underline">View all</Link>
          </div>
          <p className="text-3xl font-bold text-purple-600 mb-2">{apps.length}</p>
          <ul className="space-y-1">
            {apps.slice(0, 3).map(a => (
              <li key={a.id} className="text-xs text-gray-500 truncate">{a.job_title} · {a.company}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap gap-3">
        {profile && (
          <button
            onClick={oneShot}
            disabled={oneShotBusy}
            className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            title="Generates a general CV from your profile and opens the jobs that fit you best in your country"
          >
            {oneShotBusy ? (
              <>
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Creating CV & finding jobs…
              </>
            ) : '⚡ CV + best jobs near me'}
          </button>
        )}
        <Link to="/app/jobs" className="px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700">
          Browse jobs →
        </Link>
        <Link to="/app/generate" className="px-5 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
          Create a CV
        </Link>
      </div>
    </div>
  );
};

export default DashboardPage;
